import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  SUPPORTED_LANGS,
  DEFAULT_LANG,
  RTL_LANGS,
  dictionaries,
  isSupportedLang,
} from "../i18n";
import { useTranslationOverrides } from "../hooks/useTranslationOverrides";

const LanguageContext = createContext(null);

function lookup(dict, path) {
  if (!path) return undefined;
  return path.split(".").reduce((acc, key) => {
    if (acc && Object.prototype.hasOwnProperty.call(acc, key)) return acc[key];
    return undefined;
  }, dict);
}

export function LanguageProvider({ lang, children }) {
  const navigate = useNavigate();
  const location = useLocation();

  const currentLang = isSupportedLang(lang) ? lang : DEFAULT_LANG;
  const isRTL = RTL_LANGS.has(currentLang);
  const overrides = useTranslationOverrides(currentLang);

  // Sync <html lang/dir> on every change
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("lang", currentLang);
    html.setAttribute("dir", isRTL ? "rtl" : "ltr");
  }, [currentLang, isRTL]);

  const switchLang = useCallback(
    (target) => {
      if (!isSupportedLang(target)) return;
      // Replace the first segment of the path with the new language code.
      const parts = location.pathname.split("/").filter(Boolean);
      if (parts.length === 0) {
        parts.push(target);
      } else if (isSupportedLang(parts[0])) {
        parts[0] = target;
      } else {
        parts.unshift(target);
      }
      const nextPath = "/" + parts.join("/") + (location.pathname.endsWith("/") ? "/" : "");
      navigate(nextPath + location.search + location.hash);
    },
    [location, navigate]
  );

  const t = useCallback(
    (key, fallback) => {
      // Admin override wins
      if (overrides && Object.prototype.hasOwnProperty.call(overrides, key)) {
        const ov = overrides[key];
        if (typeof ov === "string" && ov.length > 0) return ov;
      }
      const value = lookup(dictionaries[currentLang], key);
      if (typeof value === "string") return value;
      const def = lookup(dictionaries[DEFAULT_LANG], key);
      if (typeof def === "string") return def;
      return fallback ?? key;
    },
    [currentLang, overrides]
  );

  const value = useMemo(
    () => ({
      lang: currentLang,
      isRTL,
      supportedLangs: SUPPORTED_LANGS,
      switchLang,
      t,
    }),
    [currentLang, isRTL, switchLang, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used inside <LanguageProvider>");
  }
  return ctx;
}
